import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { CommandBar } from "./CommandBar";
import { KeyboardShortcuts } from "./KeyboardShortcuts";
import { TaskBadge } from "./TaskBadge";

interface NavItem {
  to: string;
  label: string;
  /** Permissie (legacy string) die zichtbaar maakt; ontbreekt = altijd zichtbaar */
  needs?: string | string[];
  /** Ingesprongen onder het item erboven — een directe snelkoppeling. */
  sub?: boolean;
}

// 13 hoofdmodules uit PROJECT_SPEC.md sectie 8, plus Project voortgang
// onderaan (zonder permission-check, AB3).
// Modules zonder data/UI nog (Orders/Warehouse/Producten/Marketplaces/Klanten/
// Marketing & Content/Rapportages/Taken/Reconciliation/Beheer) zijn placeholders
// die het team alvast in de sidebar ziet.
const NAV: NavItem[] = [
  { to: "/dashboard",          label: "Dashboard",           needs: "dashboard.read" },
  { to: "/orders",             label: "Orders",              needs: ["orders.read", "returns.read", "overrides.read"] },
  { to: "/warehouse",          label: "Warehouse",           needs: "voorraad.read" },
  { to: "/nod-stock",          label: "NOD Stock",           needs: "wms.access" },
  { to: "/wms/mobile",         label: "TC27 Scan",           needs: "wms.scan.inbound" },
  // Direct naar het wegzetten van een pakbon: in de praktijk de enige
  // TC27-module die het magazijn gebruikt (Youp, 06-10-2026).
  { to: "/wms/mobile/inslag",  label: "Inslag",              needs: "wms.scan.inbound", sub: true },
  { to: "/producten",          label: "Producten",           needs: "assortment.view" },
  { to: "/inkoop",             label: "Inkoop",              needs: "inkoop.read" },
  { to: "/prijzen",            label: "Prijzen",             needs: ["amazon.write", "bol.write"] },
  { to: "/verkopen",           label: "Verkopen",            needs: ["amazon.read", "bol.read", "kaufland.read", "mediamarkt.read", "shopify.read"] },
  { to: "/klanten",            label: "Klanten",             needs: "customers.view" },
  { to: "/cs-inbox",           label: "CS Inbox",            needs: "cs_inbox.view" },
  { to: "/finance",            label: "Finance",             needs: "finance.read" },
  { to: "/marketing-content",  label: "Marketing & Content", needs: "marketing.read" },
  { to: "/prestatie",          label: "Prestatie",           needs: "finance.pnl" },
  { to: "/rapportages",        label: "Rapportages",         needs: "reports.view" },
  { to: "/taken",              label: "Taken",               needs: "tasks.read" },
  { to: "/reconciliation",     label: "Reconciliation",      needs: "reconcile.status" },
  { to: "/beheer",             label: "Beheer",              needs: ["users.read", "audit.read"] },
];

// Aparte item — geen permission-check, voor alle ingelogde teamleden (AB3).
const PROGRESS_ITEM: NavItem = { to: "/progress", label: "Project voortgang" };

export function AppShell() {
  const { user, logout, hasPerm } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const visibleNav = NAV.filter((n) => {
    if (!n.needs) return true;
    const needs = Array.isArray(n.needs) ? n.needs : [n.needs];
    return needs.some((p) => hasPerm(p));
  });

  return (
    <div className="app-shell">
      <aside className={`app-sidebar${sidebarOpen ? " open" : ""}`}>
        <a href="/dashboard" className="app-sidebar-logo" aria-label="Merkatura — naar dashboard">
          <img src="/logo.png" alt="Merkatura" />
        </a>
        {visibleNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            className={({ isActive }) => (isActive ? "active" : "")}
            style={n.sub ? { paddingLeft: 28, fontSize: 13 } : undefined}
          >
            {n.sub ? `↳ ${n.label}` : n.label}
          </NavLink>
        ))}
        {/* Externe link naar losstaand WMS3D-project — geen React route,
            geen import, geen bundle-impact. Browser navigeert vol pagina naar
            /wms3d/ dat Caddy proxied naar de wms3d containers. */}
        <a href="/wms3d/">WMS3D magazijn</a>
        <div style={{
          marginTop: "auto", paddingTop: 12, borderTop: "1px solid var(--color-border, #e2e8f0)",
        }}>
          <NavLink
            to={PROGRESS_ITEM.to}
            className={({ isActive }) => (isActive ? "active" : "")}
            style={{ fontSize: 13, opacity: 0.85 }}
          >
            {PROGRESS_ITEM.label}
          </NavLink>
        </div>
      </aside>

      <div
        className={`sidebar-backdrop${sidebarOpen ? " open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      <header className="app-topbar">
        <button
          className="hamburger"
          onClick={() => setSidebarOpen((s) => !s)}
          aria-label="Menu"
        >
          ☰
        </button>
        <div style={{ marginLeft: "auto" }}>
          <TaskBadge />
        </div>
        <span style={{ color: "var(--color-text-muted)", fontSize: "var(--fs-sm)" }}>
          {user?.display_name || user?.email}
          {user?.is_owner && (
            <span className="badge" style={{
              marginLeft: 8, padding: "0 6px", fontSize: 10,
              background: "#fbbf24", color: "#78350f",
            }}>
              eigenaar
            </span>
          )}
          {!user?.is_owner && user?.role && user.role !== "admin" && (
            <span className="badge" style={{ marginLeft: 8, padding: "0 6px", fontSize: 10 }}>
              {user.role}
            </span>
          )}
        </span>
        <button className="btn btn-ghost" onClick={logout}>
          Uitloggen
        </button>
      </header>
      <main className="app-content">
        <Outlet />
      </main>
      <CommandBar />
      <KeyboardShortcuts />
    </div>
  );
}
